// Step 26.5: shared reject-moment controls used by every surface that
// shows AI key_moments (KeyMomentsExplorer, Caption Library, ClipPreview,
// ReelEditor segment cards). One hook owns:
//   - the reason-category dialog state
//   - the POST /admin/dropped-moments call
//   - the Undo toast (auto-dismisses after 8s)
// Each surface passes in `onLocalRemove` / `onLocalRestore` callbacks so it
// can optimistically hide/restore its own list — the hook doesn't try to
// manage cross-surface state.
//
// Usage:
//   const reject = useReject({
//     onLocalRemove: (m) => { /* hide m from this view */ },
//     onLocalRestore: (m) => { /* put m back if reject fails */ },
//   })
//   ...
//   <Button onClick={() => reject.start(m)}>❌ Reject</Button>
//   <RejectMomentDialog hook={reject} />
//   <RejectUndoToast hook={reject} />

import { useEffect, useState } from 'react'
import { useHighlightsApi } from './useHighlightsApi'

export function useReject({ onLocalRemove, onLocalRestore } = {}) {
  const api = useHighlightsApi()
  const [rejectingMoment, setRejectingMoment] = useState(null)
  const [undoToast, setUndoToast] = useState(null)

  useEffect(() => {
    if (!undoToast) return
    const t = setTimeout(() => setUndoToast(null), 8000)
    return () => clearTimeout(t)
  }, [undoToast])

  // Open the dialog. Caller passes the moment row to be rejected.
  // The moment shape we expect:
  //   { tbtv_id, start_ms, end_ms?, moment_type?, title?, source?, detector? }
  // Anything missing is sent as null — backend just uses (tbtv_id, start_ms,
  // normalized title) for the match.
  function start(m) {
    if (!m || !m.tbtv_id || typeof m.start_ms !== 'number') {
      console.warn('useReject.start: moment missing tbtv_id or start_ms', m)
      return
    }
    setRejectingMoment(m)
  }

  function cancel() {
    setRejectingMoment(null)
  }

  async function confirm({ reason_category, reason_note }) {
    const m = rejectingMoment
    setRejectingMoment(null)
    if (!m) return
    onLocalRemove?.(m)
    try {
      const res = await api.dropMoment({
        tbtv_id: m.tbtv_id, start_ms: m.start_ms, end_ms: m.end_ms ?? null,
        moment_type: m.moment_type ?? null, title: m.title || '',
        source: m.source ?? null, detector: m.detector ?? null,
        reason_category, reason_note: reason_note || null,
      })
      const label = `${m.title || '(untitled)'} @ ${formatMs(m.start_ms)}`
      setUndoToast({ drop_id: res.drop_id, label, moment: m, category: reason_category })
    } catch (e) {
      onLocalRestore?.(m)
      // eslint-disable-next-line no-alert
      window.alert(`Reject failed: ${e.message || e}`)
    }
  }

  async function undo() {
    if (!undoToast) return
    const { drop_id, moment } = undoToast
    setUndoToast(null)
    onLocalRestore?.(moment)
    try {
      await api.undoDroppedMoment(drop_id)
      // eslint-disable-next-line no-alert
      window.alert(
        'Drop record removed. Note: the key_moments row was deleted; re-run TBTV-AI Find Key Moments to regenerate it (the drop list will no longer block it).',
      )
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(`Undo failed: ${e.message || e}`)
    }
  }

  return {
    // dialog state
    rejectingMoment, start, cancel, confirm,
    // toast state
    undoToast, undo,
  }
}

function formatMs(ms) {
  const totalS = Math.floor((ms || 0) / 1000)
  const m = Math.floor(totalS / 60)
  const s = totalS % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
